// GeoTiff file format
// see https://partners.adobe.com/public/developer/en/tiff/TIFF6.pdf
// see http://www.remotesensing.org/geotiff/spec/geotiff6.html#6.1

const FIELD_INTERPRETATION = 262;
const FIELD_SAMPLES_PER_PIXEL = 277;
const FIELD_SAMPLE_FORMAT = 339;
const FIELD_GEOTIFF_PROJECTION = 34735;

// field size in bytes
const FIELD_SIZES = {
    // 1 = BYTE
    1: 1,
    // 2 = ASCII,
    2: 1,
    // 3 = SHORT,
    3: 2,
    // 4 = LONG,
    4: 4,
    // 5 = RATIONAL,
    5: 8,
    // 6 = SBYTE
    6: 1,
    // 7 = UNDEFINED 
    7: 1,
    // 8 = SSHORT,
    8: 2,
    // 9 = SLONG,
    9: 4,
    // 10 = SRATIONAL
    10: 8,
    // 11 = FLOAT
    11: 4,
    // 12 = DOUBLE
    12: 8
};        
        
const getReader = (type, dataView) => {
    switch(type) {
       case 1:
           return dataView.getUint8;
       case 6:
           return dataView.getInt8;
       case 3:
           return dataView.getUint16;
       case 8:
           return dataView.getInt16;
       case 4:
           return dataView.getUint32;
       case 9:
           return dataView.getInt32;
       case 11:
           return dataView.getFloat32;
       case 12:
           return dataView.getFloat64;
       default:
           return dataView.getUint8;
    }
}                 

const createReader = async (reader) => {
	// parse first bytes 
	const buffer = await reader.read(0, 8);
	
	const dataView = new DataView(buffer);
	const b0 = dataView.getUint8(0);
	const b1 = dataView.getUint8(1);

	let littleEndian = undefined;

	if (b0 === 0x49 && b1 === 0x49) {
		littleEndian = true;
	} else if (b0 === 0x4D && b1 === 0x4D) {
		littleEndian = false;
	}

	const validTiff = dataView.getUint16(2, littleEndian) === 42;

	if (validTiff) {	
		const offset = dataView.getUint32(4, littleEndian);		
		return new GeotiffReader(reader, littleEndian, offset);
	} else {
		console.warn(b0, b1);
	}
};
	
class GeotiffHeader {
	constructor(directory) {
		this.directory = directory;
		console.log( directory );
	}
	
	getInterpretation() {
		return this.directory[FIELD_INTERPRETATION][0];
	}
	
	getSamplesPerPixel() {
		return this.directory[FIELD_SAMPLES_PER_PIXEL][0];
	}
	
	getSampleFormat() {
		return this.directory[FIELD_SAMPLE_FORMAT][0];
	}	
	
	hasCoordinateSystem() {
		return this.directory[FIELD_GEOTIFF_PROJECTION] !== undefined;
	}
}

class GeotiffReader {
	constructor(reader, littleEndian, offset) {
		this.reader = reader;	
		this.littleEndian = littleEndian;
		this.offset = offset;
		
		this.directory = {};
		this.entries = [];
		this.entryIndex = -1;		
	}
	
	async read() {
		const buffer = await this.reader.read(this.offset, this.offset + 2);
		const dataView = new DataView(buffer);
		
		const entries = dataView.getUint16(0, this.littleEndian);
		const contentBuffer = await this.reader.read(this.offset + 2, this.offset + 2 + (entries * 12));
					
		await this.parseEntries( contentBuffer );
		
		return new GeotiffHeader( this.directory );		
	}
	
	async parseEntries(buffer) {
		const dataView = new DataView(buffer);
		const entryLength = 12;

		for (let i = 0, l = buffer.byteLength / entryLength; i < l; i++) {
			const tag = dataView.getUint16((i * entryLength) + 0, this.littleEndian);
			const type = dataView.getUint16((i * entryLength) + 2, this.littleEndian);
			const count = dataView.getUint32((i * entryLength) + 4, this.littleEndian);
			const valueOffset = dataView.getUint32((i * entryLength) + 8, this.littleEndian);
				
			const entry = {
				tag: tag,
				type: type,
				count: count,
				valueOffset: valueOffset
			}

			this.entries.push(entry);
		}

		await this.processNextEntry();
	}
	
	async addDirectoryValue(tag, values) {
	   	this.directory[tag] = values;   		                                        
	    await this.processNextEntry();                 
	};
	
	async processNextEntry() {
		this.entryIndex++;
	    
	    if( this.entryIndex < this.entries.length ) {
	    	const { type, tag, count, valueOffset } = this.entries[this.entryIndex];
	    	const fieldSize = FIELD_SIZES[type];
	    			    		
	    	const fieldValueSize = fieldSize * count;  
	    	let value = valueOffset;
	    			
	    	if (fieldValueSize <= 4) {
	        	// The value is stored at the big end of the valueOffset.
	            if (this.littleEndian === false) {
	            	value = valueOffset >>> ((4 - fieldSize) * 8);
	            }                    
	        }  		    		
	    			    			
	    	// more then 1 value (array) or wont fit within valueOffset
	    	if (count > 1 || fieldSize > 4) {
	    	    if( count < 100 || type === 2 ) {
	    			let values = [];
					
					try {
				        // parse section
		    		    const buffer = await this.reader.read(valueOffset, valueOffset + (fieldSize * count));    			        
		    			
		    			const dataView = new DataView(buffer);
		    			const reader = getReader(type, dataView);   
		    			            
		    			for( let i = 0; i < count; i++) {    			                
		    		 		values[i] = reader.apply(dataView, [i * fieldSize, this.littleEndian]);
		    			}
	    			} catch(e) { 
	    				console.debug("Skipping tag", tag, "of type", type, ":", e);
	    			}
	    			                			            
	    			// ascii, join to string
	    			if( type === 2 ) {
	    				values = [String.fromCharCode.apply(null, values)];
		    		}    			            
	    			            
	    			await this.addDirectoryValue(tag, values);
				}  else {
	    			console.warn("Ignoring entry tag", tag, "of type", type, ":", count, "records");   
	            	await this.processNextEntry();
	            }
	   		} else {
	    		await this.addDirectoryValue(tag, [value]);
			}
	    } 
	}
}


const geotiff = async (file) => {
	const reader = await createReader(file);
	if( reader ) {
		return reader.read();
	} else {
		return null;
	}	
}

export {
	geotiff
}